import React from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { useState, useEffect } from 'react';
import { useDarkMode } from '../hooks/useDarkMode';
import { Loader2, Calendar, Bot, Key, Trash2, AlertCircle, X } from 'lucide-react';
import { toast } from '../lib/toast';
import { updateProfile, deleteAccount } from '../lib/auth';

interface SettingsProps {
  navigate: (to: string) => void;
}

function Settings({ navigate }: SettingsProps) {
  const [isDark, setDarkMode] = useDarkMode();
  const [llmKey, setLlmKey] = useState('');
  const [showLlmKey, setShowLlmKey] = useState(false);
  const [anthropicKey, setAnthropicKey] = useState('');
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [geminiKey, setGeminiKey] = useState('');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('llm_key, calendar_url, timezone, anthropic_key, gemini_key')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setLlmKey(data.llm_key || '');
        setCalendarUrl(data.calendar_url || '');
        setTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        setAnthropicKey(data.anthropic_key || '');
        setGeminiKey(data.gemini_key || '');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleSaveApiKey = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update({ 
          llm_key: llmKey.trim(),
          anthropic_key: anthropicKey.trim(),
          gemini_key: geminiKey.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      toast.success('API key saved successfully');
    } catch (error) {
      console.error('Error saving API key:', error);
      toast.error('Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCalendar = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update({ 
          calendar_url: calendarUrl.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      toast.success('Calendar URL saved successfully');
    } catch (error) {
      console.error('Error saving calendar URL:', error);
      toast.error('Failed to save calendar URL');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTimezone = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update({ 
          timezone: timezone.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      toast.success('Timezone saved successfully');
    } catch (error) {
      console.error('Error saving timezone:', error);
      toast.error('Failed to save timezone');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.email) {
        toast.error('Not authenticated');
        return;
      }

      const userEmail = session.user.email;
      if (deleteConfirmText !== userEmail) {
        toast.error('Please enter your email correctly to confirm deletion');
        return;
      }

      setDeleting(true);
      try {
        await deleteAccount();
        toast.success('Your account has been deleted');
        navigate('/auth');
      } catch (error) {
        console.error('Error deleting account:', error);
        const message = error instanceof Error ? error.message : 'Failed to delete account';
        toast.error(message);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete account';
      toast.error(message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Settings</h1>

          {/* Theme Settings */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow sm:rounded-lg mb-8">
            <div className="p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                Theme Settings
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">Dark Mode</span>
                <button
                  onClick={() => setDarkMode(!isDark)}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 focus:ring-indigo-500 ${
                    isDark ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition-transform duration-300 ease-in-out ${
                      isDark ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  >
                    <svg
                      className={`h-3 w-3 transition-opacity duration-200 ${isDark ? 'opacity-100' : 'opacity-0'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                    </svg>
                    <svg
                      className={`absolute h-3 w-3 transition-opacity duration-200 ${isDark ? 'opacity-0' : 'opacity-100'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* OpenAI API Key Settings */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow sm:rounded-lg mb-8">
            <div className="p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                API Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="openai-key" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    OpenAI API Key
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type={showLlmKey ? "text" : "password"}
                      id="openai-key"
                      value={llmKey}
                      onChange={(e) => setLlmKey(e.target.value)}
                      placeholder="Enter your OpenAI API key"
                      className="block w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLlmKey(!showLlmKey)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center cursor-pointer"
                    >
                      <Key className={`h-5 w-5 ${showLlmKey ? 'text-indigo-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {llmKey && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            const { data: { user } } = await supabase.auth.getUser();
                            if (!user) throw new Error('No user found');

                            const { error } = await supabase
                              .from('profiles')
                              .update({ 
                                llm_key: null,
                                updated_at: new Date().toISOString()
                              })
                              .eq('user_id', user.id);

                            if (error) throw error;
                            setLlmKey('');
                            toast.success('API key removed successfully');
                          } catch (error) {
                            console.error('Error removing API key:', error);
                            toast.error('Failed to remove API key');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove Key
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="anthropic-key" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Anthropic API Key
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type={showAnthropicKey ? "text" : "password"}
                      id="anthropic-key"
                      value={anthropicKey}
                      onChange={(e) => setAnthropicKey(e.target.value)}
                      placeholder="Enter your Anthropic API key"
                      className="block w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center cursor-pointer"
                    >
                      <Key className={`h-5 w-5 ${showAnthropicKey ? 'text-indigo-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {anthropicKey && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            const { data: { user } } = await supabase.auth.getUser();
                            if (!user) throw new Error('No user found');

                            const { error } = await supabase
                              .from('profiles')
                              .update({ 
                                anthropic_key: null,
                                updated_at: new Date().toISOString()
                              })
                              .eq('user_id', user.id);

                            if (error) throw error;
                            setAnthropicKey('');
                            toast.success('Anthropic API key removed successfully');
                          } catch (error) {
                            console.error('Error removing Anthropic API key:', error);
                            toast.error('Failed to remove Anthropic API key');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove Key
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="gemini-key" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Gemini API Key
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type={showGeminiKey ? "text" : "password"}
                      id="gemini-key"
                      value={geminiKey}
                      onChange={(e) => setGeminiKey(e.target.value)}
                      placeholder="Enter your Gemini API key"
                      className="block w-full pr-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGeminiKey(!showGeminiKey)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center cursor-pointer"
                    >
                      <Key className={`h-5 w-5 ${showGeminiKey ? 'text-indigo-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {geminiKey && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setSaving(true);
                            const { data: { user } } = await supabase.auth.getUser();
                            if (!user) throw new Error('No user found');

                            const { error } = await supabase
                              .from('profiles')
                              .update({ 
                                gemini_key: null,
                                updated_at: new Date().toISOString()
                              })
                              .eq('user_id', user.id);

                            if (error) throw error;
                            setGeminiKey('');
                            toast.success('Gemini API key removed successfully');
                          } catch (error) {
                            console.error('Error removing Gemini API key:', error);
                            toast.error('Failed to remove Gemini API key');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove Key
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleSaveApiKey}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        Saving API Key...
                      </>
                    ) : (
                      <>
                        <Bot className="h-5 w-5 mr-2" />
                        Save API Key
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Enter your OpenAI API key to enable AI features. Your key is stored securely and used only for your account.
                </p>
              </div>
            </div>
          </div>

          {/* Google Calendar Integration */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow sm:rounded-lg mt-8">
            <div className="p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                Google Calendar Integration
              </h3>
              <div className="max-w-xl space-y-4">
                <div>
                  <label htmlFor="calendar-url" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Public URL to this calendar
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <input
                      type="url"
                      id="calendar-url"
                      value={calendarUrl}
                      onChange={(e) => setCalendarUrl(e.target.value)}
                      placeholder="Enter your Google Calendar embed URL"
                     className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
                    />
                  </div>
                  <div className="mt-4 flex justify-end space-x-3">
                    {calendarUrl && (
                      <button
                        type="button"
                        onClick={() => setCalendarUrl('')}
                        className="text-sm text-red-600 hover:text-red-500"
                      >
                        Remove Calendar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveCalendar}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Saving Calendar...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-5 w-5 mr-2" />
                          Save Calendar
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Enter your Google Calendar embed URL to display your calendar on the dashboard.
                    <a 
                      href="https://support.google.com/calendar/answer/41207?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-500 ml-1"
                    >
                      Learn how to get the embed URL
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Timezone Settings */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow sm:rounded-lg mt-8">
            <div className="p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                Timezone Settings
              </h3>
              <div className="max-w-xl space-y-4">
                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Your Timezone
                  </label>
                  <select
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select a timezone</option>
                    {Intl.supportedValuesOf('timeZone').map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  <div className="mt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleSaveTimezone}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Saving Timezone...
                        </>
                      ) : (
                        'Save Timezone'
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Select your timezone to ensure all dates and times are displayed correctly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Account */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow sm:rounded-lg mt-8">
            <div className="p-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                Delete Account Permanently
              </h3>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Warning: This action cannot be undone
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>
                        Deleting your account will permanently remove all your data, including:
                      </p>
                      <ul className="list-disc list-inside mt-2">
                        <li>All projects and tasks</li>
                        <li>Customer information</li>
                        <li>Team members and assignments</li>
                        <li>Notes and research data</li>
                        <li>Account settings and preferences</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-5 w-5 mr-2" />
                  Delete Account
                </button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Please type your email address to confirm deletion:
                  </p>
                  <input
                    type="email"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Enter your email"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                  />
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Deleting Account...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-5 w-5 mr-2" />
                          Confirm Deletion
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Settings;